import React, { useCallback, useContext, useState } from 'react';
import { useSeo } from 'hooks/use-seo';
import { ProductInterface } from 'modules/product/interfaces/product.interface';
import Carousel from './components/carousel/carousel';
import { TeApiContext } from '@ecommerce/nest-ssr/common';
import classNames from 'classnames';
import { useModal } from 'hooks/use-modal';
import Modal from 'components/modal';
import { CATEGORY_EXCEPTIONS_IDS } from 'modules/product/category-exceptions';

const ModalCarousel = React.lazy(() => import('./components/modal-carousel/modal-carousel'));

import './product-photo-section.scss';

interface ProductPhotoCarouselProps {
    product: Pick<ProductInterface, 'name' | 'description' | 'photos' | 'attributes' | 'minPrice' | 'opinions'>
        & { category: Pick<ProductInterface['category'], 'name' | 'id'>; };
    productOffers: ProductInterface['offers'];
}

const ProductPhotoSection: React.FC<ProductPhotoCarouselProps> = ({
    product,
    productOffers
}) => {
    const seo = useSeo();
    const { isOpen: modalIsOpen, open: loadModal, close: closeModal } = useModal('PHOTO_CAROUSEL');
    const api = useContext(TeApiContext);

    if (api === null) {
        throw new Error('Invalid API context');
    }

    const isMobile = api.getDevice() === 'mobile';
    const [selectedItem, setSelectedItem] = useState(0);

    const h1 = seo.h1 || product.name;

    const onLoadModal = useCallback(
        (index: number) => {
            setSelectedItem(index);
            loadModal();
        }, [setSelectedItem, loadModal]
    );

    return (
        <>
            <Carousel
                categoryId={product.category.id}
                alt={`${h1} - ${product.category.name}`}
                title={`${h1} - ${product.category.name}`}
                photos={product.photos.items}
                emulateTouch={false}
                onClick={CATEGORY_EXCEPTIONS_IDS.includes(product.category.id) ? void 0 : onLoadModal}
                showThumbs={product.photos.items.length > 1 && !isMobile}
                showArrows={isMobile}
                altWithCounter={true}
                showStatus={product.photos.items.length > 1 && isMobile}
                className={classNames({
                    'carousel-root--single': product.photos.items.length === 1 && isMobile,
                    'carousel-root--hide-icon-search': CATEGORY_EXCEPTIONS_IDS.includes(product.category.id)
                })}
            />
            {
                modalIsOpen && (
                    <Modal
                        showCloseIcon={true}
                        center={true}
                        isOpen={true}
                        onClose={closeModal}
                        shouldCloseOnOverlayClick={!isMobile}
                        modalClassName="photo-modal-carousel"
                    >
                        <ModalCarousel
                            categoryId={product.category.id}
                            productName={h1}
                            categoryName={product.category.name}
                            productAttributes={product.attributes}
                            productOffers={productOffers}
                            photos={product.photos}
                            emulateTouch={false}
                            selectedItem={selectedItem}
                        />
                    </Modal>
                )
            }
        </>
    );
};

export default ProductPhotoSection;
